import { useMemo, useState, useEffect, useCallback } from "react";
import { ICrewEvent } from "src/redux/reducers/CrewScheduleReducer";
import { useDispatch, useSelector } from "react-redux";
import { INonFlyingAssignment, IUser, ICrewsNonFlyingAssignment, IDisplayBidNFA } from "@vmosolutions/oc-interfaces";
import { ISummaryItem } from "../../FlightSummaryList/SummaryList";
import CrewScheduleEventSummaryList, { ISummarySubHeader } from "../CrewScheduleEventSummaryList";
import moment from "moment-timezone";
import { CardContent, MenuItem, Typography, Button, Grid, Theme } from "@mui/material";
import { removeNFAFromCrew } from "src/redux/actions/CrewSchedule/nonFlyingAssignments";
import {
  convertToPlannedNFA,
  deletePlannedEvent,
  setSelectedCrewEvents,
  convertNfaToPairing,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { openHistoryWindow, openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import { NonFlightChip } from "../../Pairings/NonFlightChip";
import { openSnackbar } from "src/redux/actions/App.Actions";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import { updateCrewBidEvent } from "src/redux/actions/Bidding.Actions";
import chroma from "chroma-js";

interface INonFlyingAssignmentProps {
  id: string;
  path: string[];
  nonFlyingAssignmentEvents: ICrewEvent[];
  embedded?: boolean;
}

export const NonFlyingAssignmentContext = (props: INonFlyingAssignmentProps): JSX.Element => {
  const { nonFlyingAssignmentEvents, path, id, embedded } = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [selectedCrewEvent, setSelectedCrewEvent] = useState<ICrewEvent>();
  const selectedNFA = selectedCrewEvent?.event as ICrewsNonFlyingAssignment | IDisplayBidNFA;

  const isBidNFA = (selectedNFA as IDisplayBidNFA)?.hasOwnProperty("prefRank");

  const pointingUpGreen =
    isBidNFA &&
    ((!(selectedNFA as IDisplayBidNFA)?.assigned && !(selectedNFA as IDisplayBidNFA)?.awarded) ||
      ((selectedNFA as IDisplayBidNFA)?.assigned && (selectedNFA as IDisplayBidNFA)?.awarded));
  const assignedBidNFA = isBidNFA && (selectedNFA as IDisplayBidNFA)?.assigned;
  const awardedBidNFA = isBidNFA && (selectedNFA as IDisplayBidNFA)?.awarded;

  // If context isn't being used in it's embedded form but rather as a context menu.
  // Select the first even available in the array as the default selection.
  useEffect(() => {
    if (!embedded) {
      const defaultSelection = nonFlyingAssignmentEvents?.length > 0 ? nonFlyingAssignmentEvents[0] : null;
      setSelectedCrewEvent(defaultSelection);
    }
  }, [nonFlyingAssignmentEvents, embedded]);

  const crewMemberOnSelectedNFA = selectedCrewEvent?.crewId;
  const isPlanned: boolean = selectedCrewEvent?.isPlanned;
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  const { timezoneId } = displayTimezone;

  const summaryItems: Array<ISummaryItem | ISummarySubHeader> = useMemo(
    () =>
      selectedNFA && !embedded
        ? [
            {
              key: `${selectedNFA._id}-type`,
              value: selectedNFA.assignmentType.title,
              label: "Type",
            },
            {
              key: `${selectedNFA._id}-base`,
              value: selectedNFA.locationICAO ?? "Unspecified",
              label: "Base",
            },
            {
              key: `${selectedNFA._id}-start`,
              value: moment.utc(selectedNFA.start).tz(timezoneId).format("D MMM, YYYY HH:mm z"),
              label: "Start",
            },
            {
              key: `${selectedNFA._id}-end`,
              value: moment.utc(selectedNFA.end).tz(timezoneId).format("D MMM, YYYY HH:mm z"),
              label: "End",
            },
            ...selectedNFA.crew.map((crew: IUser) => ({
              key: `${selectedNFA._id}-${crew._id}`,
              value: ` ${crew.lastName}, ${crew.firstName}`,
              label: "Assigned To Crew",
            })),
          ]
        : null,
    [embedded, selectedNFA, timezoneId]
  );

  const addAssignment = (): void => {
    // open menu to edit selected pairing
    if (!embedded) dispatch(closeContextMenu(id));
    dispatch(openWindow("Add Non-Flying Assignment", "NFAEditorWindow", path, { nfaId: null }, "context"));
  };

  const editAssignment = (): void => {
    // open menu to edit selected pairing
    if (!embedded) dispatch(closeContextMenu(id));
    dispatch(openWindow("Edit Non-Flying Assignment", "NFAEditorWindow", path, { nfaId: selectedNFA?._id }, "context"));
  };

  const convertToPairing = async (): Promise<void> => {
    setIsLoading(true);
    const nfaIds: string[] = [];
    let isNFAsOnMultipleCrews = false;
    for (const nfa of nonFlyingAssignmentEvents) {
      if (nfa.crewId !== crewMemberOnSelectedNFA) {
        isNFAsOnMultipleCrews = true;
        break;
      } else {
        nfaIds.push(nfa?.event?._id);
      }
    }
    if (isNFAsOnMultipleCrews) {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Cannot create a pairing from NFAs on multiple crew members",
        })
      );
    } else {
      const response = await convertNfaToPairing({
        nfaIds,
        crewId: crewMemberOnSelectedNFA,
      });
      if (response?._id) {
        if (!embedded) dispatch(closeContextMenu(id));
        dispatch(
          openWindow(
            "Edit Pairing",
            "PairingEditorWindow",
            [],
            { pairingId: response?._id },
            "context",
            true,
            MosaicDirection.Right,
            940
          )
        );
      } else {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Something went wrong in converting the Non Flying Assignment to a Pairing",
          })
        );
      }
    }
    setIsLoading(false);
  };

  const convertToPlanned = async (): Promise<void> => {
    setIsLoading(true);
    if (embedded) {
      const response = await convertToPlannedNFA(selectedNFA._id, [crewMemberOnSelectedNFA]);
      if (response?.[0])
        dispatch(
          setSelectedCrewEvents(
            [{ event: response[0], crewId: crewMemberOnSelectedNFA, isPlanned: true }],
            false,
            false,
            false
          )
        );
    } else {
      const selectedEvents = [];
      for (const event of nonFlyingAssignmentEvents) {
        const response = await convertToPlannedNFA(event.event._id, [event.crewId]);
        if (response?.[0]) selectedEvents.push({ event: response[0], crewId: event.crewId, isPlanned: true });
      }
      dispatch(closeContextMenu(id));
      dispatch(setSelectedCrewEvents(selectedEvents, false, false, false));
    }
    setIsLoading(false);
  };

  const openHistory = useCallback((): void => {
    if (selectedNFA?._id) {
      // dispatch(openDrawer());
      dispatch(
        openHistoryWindow(
          "Assignment History",
          {
            collection: "nonflyingassignments",
            id: selectedNFA._id,
            assignment: selectedNFA as ICrewsNonFlyingAssignment,
          },
          []
        )
      );
    }
  }, [dispatch, selectedNFA]);

  const removeAssignment = async (): Promise<void> => {
    setIsLoading(true);

    const plural = embedded ? "" : nonFlyingAssignmentEvents?.length > 0 ? "s" : "";
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "info",
        message: `Removing NFA${plural}`,
      })
    );

    if (embedded) {
      await removeNFAFromCrew(selectedNFA._id, crewMemberOnSelectedNFA);
    } else {
      const promises: Promise<any>[] = [];
      for (const event of nonFlyingAssignmentEvents) {
        promises.push(removeNFAFromCrew(event.event._id, event.crewId));
      }
      await Promise.all(promises);
    }

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: `NFA${plural} Removed Successfully.`,
      })
    );
    // Await resolution of all assignments removed then clear selected crew events.
    dispatch(setSelectedCrewEvents([], false, false, false));
    setIsLoading(false);
  };

  const handleRemovePlannedEvent = async (): Promise<void> => {
    setIsLoading(true);

    const plural = nonFlyingAssignmentEvents?.length > 0 ? "s" : "";
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "info",
        message: `Removing Planned Event${plural}`,
      })
    );

    const promises: Promise<any>[] = [];
    for (const event of nonFlyingAssignmentEvents) {
      if (event.isPlanned) {
        promises.push(deletePlannedEvent(event));
      } else {
        promises.push(removeNFAFromCrew(event.event._id, event.crewId));
      }
    }
    await Promise.all(promises);
    // Await resolution of all assignments removed then clear selected crew events.
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: `Planned Event${plural} Removed Successfully.`,
      })
    );
    dispatch(setSelectedCrewEvents([], false, false, false));
    setIsLoading(false);
  };

  /** Function to toggle bid assignment status of bidPairing. */
  const toggleAssignBidNFA = useCallback(() => {
    if (!isBidNFA) {
      return;
    }
    const bidPairingEvent = { ...selectedCrewEvent };
    const updatedBidPairing: IDisplayBidNFA = {
      ...(bidPairingEvent.event as IDisplayBidNFA),
      assigned: !(bidPairingEvent.event as IDisplayBidNFA).assigned,
    };
    const updatedCrewEvent: ICrewEvent = { ...bidPairingEvent, event: updatedBidPairing };
    dispatch(updateCrewBidEvent(updatedCrewEvent, "nfa"));
    dispatch(setSelectedCrewEvents([updatedCrewEvent], false, false, false));
  }, [dispatch, isBidNFA, selectedCrewEvent]);

  if (!selectedCrewEvent && !embedded) {
    return <></>;
  }

  const baseKey = `${props.id}-${selectedNFA?._id}-contextMenu-`;

  return (
    <>
      {!embedded && nonFlyingAssignmentEvents.length === 1 && (
        <CrewScheduleEventSummaryList
          key={`${props.id}-${selectedNFA._id}-summaryList`}
          items={summaryItems}
          hideTitle={true}
        />
      )}

      <CardContent style={{ width: "100%", padding: 0 }}>
        <Grid container style={{ margin: "5px 0px 15px 0px" }}>
          {embedded &&
            nonFlyingAssignmentEvents?.length > 0 &&
            nonFlyingAssignmentEvents.map((crewEvent: ICrewEvent, index: number) => {
              const nonFlying = crewEvent.event as INonFlyingAssignment;
              const isSelected = crewEvent.event?._id === selectedCrewEvent?.event?._id;
              return (
                <Grid
                  item
                  key={index}
                  style={{ border: isSelected ? "1px solid white" : "" }}
                  onClick={() => setSelectedCrewEvent(isSelected ? undefined : crewEvent)}
                >
                  <NonFlightChip data={nonFlying} />
                </Grid>
              );
            })}
        </Grid>
        {embedded && selectedCrewEvent && (
          <>
            <Button
              key={`${baseKey}editAsgBtn`}
              variant="outlined"
              style={{
                margin: "0px 5px",
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 300,
              }}
              disabled={isLoading}
              onClick={editAssignment}
            >
              Edit
            </Button>

            <Button
              key={`${baseKey}convertToPlannedBtn`}
              variant="outlined"
              style={{
                margin: "0px 5px",
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 300,
              }}
              disabled={isLoading}
              onClick={convertToPlanned}
            >
              Convert To Planned
            </Button>

            <Button
              key={`${baseKey}removeAsgBtn`}
              variant="outlined"
              style={{
                margin: "0px 5px",
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 300,
              }}
              disabled={isLoading}
              onClick={removeAssignment}
            >
              Remove
            </Button>
          </>
        )}
        {embedded && !selectedCrewEvent && (
          <>
            <Button
              key={`${baseKey}addAsgBtn`}
              variant="outlined"
              style={{
                margin: "0px 5px",
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 300,
              }}
              disabled={isLoading}
              onClick={addAssignment}
            >
              Add NFA To Selected Crew/Day(s)
            </Button>
          </>
        )}
        {!isPlanned && !embedded && !isBidNFA && (
          <>
            {" "}
            <Grid
              item
              xs={12}
              sx={(theme) => ({
                backgroundColor: chroma(theme.palette.background.default).darken(0.5).css(),
                p: 0.5,
                pl: 2,
                borderBottom: `1px solid  ${chroma(theme.palette.background.default).brighten(0.5).css()}`,
              })}
            >
              Actions
            </Grid>
            {nonFlyingAssignmentEvents.length === 1 && (
              <MenuItem
                key={`${baseKey}editAsgBtn`}
                disabled={isLoading}
                onClick={editAssignment}
                sx={{ fontSize: "0.9em" }}
              >
                Edit Assignment
              </MenuItem>
            )}
            <MenuItem
              sx={{ fontSize: "0.9em" }}
              key={`${baseKey}convertToPairingBtn`}
              disabled={isLoading}
              onClick={convertToPairing}
            >
              Convert To Pairing
            </MenuItem>
            <MenuItem
              sx={{ fontSize: "0.9em" }}
              key={`${baseKey}convertToPlannedBtn`}
              disabled={isLoading}
              onClick={convertToPlanned}
            >
              Convert To Planned
            </MenuItem>
            {nonFlyingAssignmentEvents.length === 1 && (
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${baseKey}openHistoryBtn`}
                disabled={isLoading}
                onClick={openHistory}
              >
                View Assignment History
              </MenuItem>
            )}
            <MenuItem
              sx={{ fontSize: "0.9em" }}
              key={`${baseKey}removeAsgBtn`}
              disabled={isLoading}
              onClick={removeAssignment}
            >
              {`Remove Assignment${nonFlyingAssignmentEvents.length > 1 ? "s" : ""}`}
            </MenuItem>
          </>
        )}
        {!isPlanned && !embedded && isBidNFA && (
          <>
            <MenuItem
              key={`${props.id}-${selectedNFA._id}-contextMenu-bidNFAToggleAssignBtn`}
              disabled={isLoading}
              onClick={toggleAssignBidNFA}
              sx={(theme) => ({
                color: pointingUpGreen ? theme.custom.forms.dirtyColor : theme.palette.error.main,
                fontSize: "0.9em",
              })}
            >
              <DoubleArrowIcon
                style={{
                  color: "inherit",
                  transform: pointingUpGreen ? "rotate(-90deg)" : "rotate(90deg)",
                  marginRight: 5,
                  width: 14,
                }}
              />
              {awardedBidNFA && assignedBidNFA
                ? "Unstage change to Non-Flying Assignment Bid"
                : awardedBidNFA && !assignedBidNFA
                  ? "Stage unaward of Non-Flying Assignment Bid"
                  : !awardedBidNFA && assignedBidNFA
                    ? "Unstage change to Non-Flying Assignment Bid"
                    : "Stage Award of Non-Flying Assignment Bid"}
            </MenuItem>
          </>
        )}
        {isPlanned && !embedded && (
          <MenuItem
            sx={{ fontSize: "0.9em" }}
            key={`${baseKey}deletePlannedAsgBtn`}
            disabled={isLoading}
            onClick={handleRemovePlannedEvent}
          >
            Remove Assignment
          </MenuItem>
        )}
      </CardContent>
    </>
  );
};
